import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Area, AreaChart, Bar, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";
import { MapPin, ChevronDown, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { TONE_DOT, toneOf } from "@/lib/labels";
import { nok } from "@/lib/format";
import { CalendarCheck, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CoverageSummary, SourcingPlan, SupportChecks, RiskList, Verdict, RequestLine, quoteUrlForPlan, planUrl } from "@/components/capacity/CapacityBits";

const TONE_TEXT = { success: "text-emerald-700 dark:text-emerald-300", warning: "text-amber-700 dark:text-amber-300", danger: "text-rose-600 dark:text-rose-400", primary: "text-primary" };

/** Key numbers – typographic, no tiles. */
export const KpisWidget = ({ w }) => (
  <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4" data-testid="ai-widget-kpis">
    {w.items.map((it, i) => (
      <div key={i} className="min-w-0">
        <div className={cn("truncate font-semibold tracking-tight tabular", it.small ? "text-[14px] leading-6" : "text-[22px] leading-7", TONE_TEXT[it.tone])} title={String(it.value)}>
          {it.value}
        </div>
        <div className="text-[11.5px] leading-4 text-muted-foreground">{it.label}</div>
        {it.sub && <div className="truncate text-[11px] leading-4 text-muted-foreground/80">{it.sub}</div>}
      </div>
    ))}
  </div>
);

const Cell = ({ col, row }) => {
  const v = row[col.key];
  if (col.kind === "status") {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <span className={cn("h-1.5 w-1.5 rounded-full", TONE_DOT[toneOf(row._status)] || TONE_DOT.neutral)} aria-hidden />
        {v}
      </span>
    );
  }
  if (col.kind === "money") return <span className="tabular">{typeof v === "number" ? nok(v) : v}</span>;
  return <span className={cn(col.align === "right" && "tabular")}>{v ?? ""}</span>;
};

/** Grounded table from a tool. Rows with `_url` are clickable deep-links. Collapses after 8 rows. */
export const TableWidget = ({ w, dense }) => {
  const navigate = useNavigate();
  const [all, setAll] = useState(false);
  const limit = dense ? 6 : 10;
  const rows = all ? w.rows : w.rows.slice(0, limit);
  return (
    <div className="overflow-hidden rounded-xl ring-1 ring-black/[0.05] dark:ring-white/10" data-testid="ai-widget-table">
      {w.title && (
        <div className="flex items-baseline justify-between bg-muted/50 px-3 py-2 text-[12px] font-medium">
          <span>{w.title}</span>
          <span className="text-muted-foreground tabular">{w.rows.length}</span>
        </div>
      )}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-[11px] text-muted-foreground">
              {w.columns.map((c) => (
                <th key={c.key} className={cn("whitespace-nowrap px-3 py-1.5 font-medium", c.align === "right" && "text-right")}>
                  {c.label || c.key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={i}
                onClick={() => r._url && navigate(r._url)}
                className={cn("border-t border-border/60 dark:border-white/5", r._url && "cursor-pointer transition-colors duration-150 hover:bg-muted/60")}
                data-testid="ai-table-row"
              >
                {w.columns.map((c) => (
                  <td key={c.key} className={cn("max-w-[260px] truncate px-3 py-1.5 align-top", c.align === "right" && "text-right")} title={String(r[c.key] ?? "")}>
                    <Cell col={c} row={r} />
                  </td>
                ))}
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={w.columns.length} className="px-3 py-3 text-center text-muted-foreground">
                  Ingen rader
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {w.rows.length > limit && (
        <button type="button" onClick={() => setAll((v) => !v)} className="flex w-full items-center justify-center gap-1 border-t border-border/60 py-1.5 text-[11.5px] text-muted-foreground transition-colors duration-150 hover:bg-muted/50 hover:text-foreground dark:border-white/5" data-testid="ai-table-more">
          {all ? "Vis færre" : `Vis alle ${w.rows.length}`} <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", all && "rotate-180")} />
        </button>
      )}
      {w.note && <div className="px-3 py-1.5 text-[11px] text-muted-foreground">{w.note}</div>}
    </div>
  );
};

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-card px-3 py-2 text-[12px] shadow-pop">
      <div className="font-medium">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="text-muted-foreground tabular">
          {p.name}: <span className="text-foreground">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

/** Stacked area – capacity over time. */
export const ChartWidget = ({ w }) => (
  <div className="rounded-xl bg-muted/40 p-3 dark:bg-muted/20" data-testid="ai-widget-chart">
    {w.title && <div className="mb-2 text-[12px] font-medium">{w.title}</div>}
    <div className="h-[150px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={w.data} margin={{ top: 6, right: 4, left: 4, bottom: 0 }}>
          <XAxis dataKey="x" tick={{ fontSize: 10.5, fill: "#8a93a3" }} axisLine={false} tickLine={false} interval={1} />
          <YAxis hide domain={[0, w.total || "auto"]} />
          <Tooltip content={<ChartTip />} cursor={{ stroke: "rgba(16,24,40,0.15)" }} />
          {w.total && <ReferenceLine y={w.total} stroke="#c9cfda" strokeDasharray="3 3" />}
          {(w.series || []).map((s, i) => (
            <Area key={s.key} type="monotone" dataKey={s.key} name={s.label} stackId="a" stroke="#2670F2" strokeOpacity={i === 0 ? 1 : 0.4} strokeWidth={i === 0 ? 1.5 : 1} fill="#2670F2" fillOpacity={i === 0 ? 0.35 : 0.12} isAnimationActive={false} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const BAR_TONE = { primary: "#2670F2", success: "#10B981", danger: "#F43F5E", warning: "#F59E0B", muted: "#C9CFDA", ink: "#1F2446" };
const compactKr = (v) => {
  const n = Math.abs(Number(v) || 0);
  const sign = Number(v) < 0 ? "−" : "";
  if (n >= 1e6) return `${sign}${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1).replace(".", ",")} MNOK`;
  if (n >= 1e3) return `${sign}${Math.round(n / 1e3)} kkr`;
  return `${sign}${Math.round(n)} kr`;
};

const BarsTip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-card px-3 py-2 text-[12px] shadow-pop">
      <div className="font-medium">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-muted-foreground tabular">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.color || p.stroke }} aria-hidden />
          {p.name}: <span className="text-foreground">{unit === "kr" ? nok(Math.abs(p.value)) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

/** Grouped/stacked bars (finance: cashflow, budget vs actual, revenue by month) with optional balance line. */
export const BarsWidget = ({ w }) => {
  const data = (w.rows || []).map((r) => {
    const o = { ...r };
    (w.series || []).forEach((s) => {
      if (s.negative) o[s.key] = -Math.abs(Number(r[s.key]) || 0);
    });
    return o;
  });
  const hasLine = !!w.line?.key;
  return (
    <div className="rounded-xl bg-muted/40 p-3 dark:bg-muted/20" data-testid="ai-widget-bars">
      {w.title && <div className="mb-2 text-[12px] font-medium">{w.title}</div>}
      <div className="h-[170px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 6, right: 4, left: 4, bottom: 0 }} barCategoryGap={w.rows?.length > 14 ? 2 : 6}>
            <XAxis dataKey="label" tick={{ fontSize: 10.5, fill: "#8a93a3" }} axisLine={false} tickLine={false} interval={w.rows?.length > 14 ? 1 : 0} />
            <YAxis yAxisId="bars" hide />
            {hasLine && <YAxis yAxisId="line" orientation="right" hide />}
            <Tooltip content={<BarsTip unit={w.unit} />} cursor={{ fill: "rgba(16,24,40,0.05)" }} />
            <ReferenceLine yAxisId="bars" y={0} stroke="#c9cfda" />
            {(w.series || []).map((s) => (
              <Bar key={s.key} yAxisId="bars" dataKey={s.key} name={s.label} stackId={w.stacked ? "a" : undefined} fill={BAR_TONE[s.tone] || BAR_TONE.primary} fillOpacity={s.tone === "muted" ? 0.9 : 0.85} radius={w.stacked ? 0 : [3, 3, 0, 0]} isAnimationActive={false} maxBarSize={28} />
            ))}
            {hasLine && <Line yAxisId="line" type="monotone" dataKey={w.line.key} name={w.line.label || w.line.key} stroke="#1F2446" strokeWidth={1.5} dot={false} isAnimationActive={false} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {(w.series || []).map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: BAR_TONE[s.tone] || BAR_TONE.primary }} aria-hidden /> {s.label}
          </span>
        ))}
        {hasLine && (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-[2px] w-3 rounded-full bg-foreground" aria-hidden /> {w.line.label || w.line.key}
          </span>
        )}
        {w.unit === "kr" && data.length > 0 && hasLine && <span className="ml-auto tabular">{w.line.label}: {compactKr(data[data.length - 1][w.line.key])}</span>}
      </div>
    </div>
  );
};

/** «Vis på kartet» – jumps to /kart focused on the project(s). */
export const MapWidget = ({ w }) => {
  const navigate = useNavigate();
  const pid = w.project_ids?.[0];
  return (
    <button type="button" onClick={() => navigate(pid ? `/kart?prosjekt=${pid}` : "/kart")} className="group inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-[12px] font-medium transition-colors duration-150 hover:bg-primary/10 hover:text-primary" data-testid="ai-widget-map">
      <MapPin className="h-3.5 w-3.5" strokeWidth={1.75} /> {w.label ? `Vis ${w.label} på kartet` : "Vis på kartet"} <ArrowUpRight className="h-3.5 w-3.5 opacity-60" />
    </button>
  );
};

/** Engine plan in the dock: verdict, four facts, first phases, risks – and the three actions. The assistant explains; the engine decided. */
export const CapacityPlanWidget = ({ w, dense }) => {
  const navigate = useNavigate();
  const { can } = useAuth();
  const plan = { ...w, id: w.plan_id };
  return (
    <div className="rounded-xl bg-muted/40 p-3.5 dark:bg-muted/20 sm:p-4" data-testid="assistant-capacity-plan-widget">
      <RequestLine request={w.request} className="mb-2" />
      <Verdict plan={plan} compact />
      <CoverageSummary plan={plan} dense className="mt-4" />
      <div className="mt-4 border-t border-border/70 pt-1">
        <SourcingPlan plan={plan} compact limit={dense ? 2 : 3} />
      </div>
      {w.support?.length > 0 && <SupportChecks support={w.support} compact className="mt-3 border-t border-border/70 pt-3" />}
      {w.risks?.length > 0 && <RiskList risks={w.risks} limit={2} className="mt-3 border-t border-border/70 pt-3" testId="assistant-capacity-plan-risks" />}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border/70 pt-3">
        <Button size="sm" variant="ghost" className="h-8" onClick={() => navigate(planUrl(plan))} data-testid="assistant-capacity-plan-view-button">Vis kapasitetsplan</Button>
        {can("planning:write") && <Button size="sm" className="h-8" onClick={() => navigate(planUrl(plan, { reserver: "1" }))} data-testid="assistant-capacity-plan-reserve-button"><CalendarCheck className="h-3.5 w-3.5" /> Reserver kapasitet</Button>}
        {can("leads:write") && <Button size="sm" variant="secondary" className="h-8" onClick={() => navigate(quoteUrlForPlan(plan))} data-testid="assistant-capacity-plan-create-quote-button"><FileText className="h-3.5 w-3.5" /> Lag tilbud</Button>}
      </div>
    </div>
  );
};

export const Widget = ({ w, dense }) => {
  if (!w) return null;
  if (w.type === "capacity_plan") return <CapacityPlanWidget w={w} dense={dense} />;
  if (w.type === "kpis") return <KpisWidget w={w} />;
  if (w.type === "table") return <TableWidget w={w} dense={dense} />;
  if (w.type === "chart") return <ChartWidget w={w} />;
  if (w.type === "map") return <MapWidget w={w} />;
  if (w.type === "bars") return <BarsWidget w={w} />;
  return null;
};
